import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import departmentsApi, { CreateDepartmentRequest, UpdateDepartmentRequest } from '@/services/apis/departments';

// Query keys
export const departmentKeys = {
  all: ['departments'] as const,
  lists: () => [...departmentKeys.all, 'list'] as const,
  detail: (id: string) => [...departmentKeys.all, 'detail', id] as const,
};

export const useDepartments = () => {
  return useQuery({
    queryKey: departmentKeys.lists(),
    queryFn: async () => {
      const response = await departmentsApi.getDepartments();
      return response.data!;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useDepartment = ({ id, shouldSkip }: { id: string; shouldSkip: boolean }) => {
  return useQuery({
    queryKey: departmentKeys.detail(id),
    queryFn: async () => {
      const response = await departmentsApi.getDepartment(id);
      return response.data!;
    },
    enabled: !shouldSkip && !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async (data: CreateDepartmentRequest) => {
      const response = await departmentsApi.createDepartment(data);
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      addToast({
        title: t('departments.actions.createSuccess', { defaultValue: 'Department created successfully' }),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error creating department:', error);
      addToast({
        title: t('departments.actions.createError', { defaultValue: 'Failed to create department' }),
        color: 'danger',
      });
    },
  });
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateDepartmentRequest }) => {
      const response = await departmentsApi.updateDepartment(id, data);
      return response.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(data.id) });
      addToast({
        title: t('departments.actions.updateSuccess', { defaultValue: 'Department updated successfully' }),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error updating department:', error);
      addToast({
        title: t('departments.actions.updateError', { defaultValue: 'Failed to update department' }),
        color: 'danger',
      });
    },
  });
};

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('admin');

  return useMutation({
    mutationFn: async (id: string) => {
      await departmentsApi.deleteDepartment(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      addToast({
        title: t('departments.actions.deleteSuccess', { defaultValue: 'Department deleted successfully' }),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      console.error('Error deleting department:', error);
      addToast({
        title: t('departments.actions.deleteError', { defaultValue: 'Failed to delete department' }),
        color: 'danger',
      });
    },
  });
};
